import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Form, FormItemProps, Input, Modal, Select } from "antd";
import {
  checkIsLoading,
  combineClasses,
  formatNumber,
  getSelectOptions,
  toDisplayFormat,
} from "../../../../lib/utils";
import { useAppDispatch, useAppSelector } from "../../../..";
import InputNumberFormat from "../../../../components/common/InputNumberFormat";
import { FormInfo } from "../../../../components/common/FormInfo/FormInfo";
import { useFormData } from "../../../../lib/hooks/useFormData";
import {
  accountPaymentDetailsActions,
  fetchAgreementBills,
  fetchAgreementServices,
} from "../../model/slice/accountPaymentDetailsSlice";
import { DataTooltip } from "../../../../components/common/DataTooltip/DataTooltip";
import { CreateGoalBillData } from "../../model/types/CreateGoalBillData";
import { CreateGoalOfferData } from "../../model/types/CreateGoalOfferData";
import { CreateGoalServiceData } from "../../model/types/CreateGoalServiceData";
import "./CreateGoalModal.less";

interface FormData {
  paymentGoalTypeId: number;
  serviceId: number;
  billId: number;
  priority: string;
  amountForPaymentGoal: string;
  reason?: string;
  goalAmountCalculate: boolean;
  includePastDates?: boolean;
}

const formItemProps: FormItemProps = {
  className: "form-item-required__hidden-error",
  labelCol: {
    span: 10,
  },
  wrapperCol: {
    span: 14,
  },
};

interface ComponentProps {
  className?: string;
  visible: boolean;
  isLoading: boolean;
  closeModal: () => void;
  submitCreateBillGoal: (data: CreateGoalBillData) => void;
  submitCreateOfferGoal: (data: CreateGoalOfferData) => void;
  submitCreateServiceGoal: (data: CreateGoalServiceData) => void;
}

export const CreateGoalModal = (props: ComponentProps) => {
  const {
    className,
    closeModal,
    isLoading,
    visible,
    submitCreateBillGoal,
    submitCreateOfferGoal,
    submitCreateServiceGoal,
  } = props;

  const dispatch = useAppDispatch();

  const {
    payment,
    agreementBillsLoading,
    agreementBills,
    agreementServicesLoading,
    agreementServices,
  } = useAppSelector((state) => state.accountPaymentDetails);

  console.log({ payment });

  const paymentGoalTypes = useAppSelector((state) => state.dicts.paymentGoalTypes).filter(
    ({ id }) => ![3, 4].includes(id)
  );

  const agreementBillList = useMemo(
    () =>
      agreementBills?.map((item) => {
        const { id, billNumber, periodFrom, periodTo } = item;
        const period = `${toDisplayFormat(periodFrom)}-${toDisplayFormat(periodTo)}`;
        const amount = `${formatNumber(item.amount)}`;
        const amountLeft = `${formatNumber(item.amountLeft)}`;
        const labelText = [id, billNumber, period, amountLeft].join(" - ");
        return {
          ...item,
          label: (
            <DataTooltip
              dataObject={{ ...item, period, amount, amountLeft }}
              includedFields={["billNumber", "period", "amount", "amountLeft", "id"]}
              placement="topLeft"
            >
              {labelText}
            </DataTooltip>
          ),
        };
      }),
    [agreementBills]
  );

  const agreementServiceList = useMemo(
    () =>
      agreementServices?.map((item) => {
        const { id, serviceType, offering, fromDate, toDate } = item;
        const period = `${toDisplayFormat(fromDate)}-${toDisplayFormat(toDate)}`;
        const serviceTypeName = serviceType.name;
        const offeringExtName = offering.extName;
        const labelText = [id, serviceTypeName, offeringExtName].join(" - ");
        return {
          ...item,
          label: (
            <DataTooltip
              dataObject={{ ...item, serviceTypeName, offeringExtName, period }}
              includedFields={["serviceTypeName", "offeringExtName", "period", "id"]}
              placement="topLeft"
            >
              {labelText}
            </DataTooltip>
          ),
        };
      }),
    [agreementServices]
  );

  const [form] = Form.useForm<FormData>();

  const { formData, onValuesChange, setFormFieldsValue, resetFormFields } = useFormData<FormData>({
    form,
    initialValues: { goalAmountCalculate: true },
  });

  // Сбросы по закрытии
  useEffect(() => {
    if (!visible) {
      dispatch(accountPaymentDetailsActions.cleanAgreementBills());
      dispatch(accountPaymentDetailsActions.cleanAgreementServices());
      resetFormFields();
    }
  }, [visible]);

  // Загрузка счетов
  useEffect(() => {
    const agreementId = payment?.agreement.id;
    const paymentGoalTypeId = formData?.paymentGoalTypeId;
    if (agreementId && paymentGoalTypeId && [1, 2].includes(paymentGoalTypeId)) {
      dispatch(
        fetchAgreementBills({
          agreementId,
          billTypeId: paymentGoalTypeId,
          payStatusIds: [0, 1],
          billStatusIds: [1, 2, 3, 4, 5],
        })
      );
    }
  }, [formData?.paymentGoalTypeId]);

  // Загрузка предложений
  useEffect(() => {
    const agreementId = payment?.agreement.id;
    const paymentGoalTypeId = formData?.paymentGoalTypeId;
    if (agreementId && paymentGoalTypeId && [5, 6].includes(paymentGoalTypeId)) {
      dispatch(
        fetchAgreementServices({
          agreementId,
          includeFutureDates: !formData.includePastDates,
          includePastDates: !!formData.includePastDates,
          extendedInfo: true,
          extendedOfferingInfo: false,
          withChars: false,
          serviceStatusId: [1, 2, 4, 103],
        })
      );
    }
  }, [formData?.paymentGoalTypeId, formData?.includePastDates]);

  const paymentData = useMemo(() => {
    if (payment) {
      // const amount = Number(payment.amount) || 0;
      const availableAmount = payment.extra?.availableAmount
        ? Number(payment.extra.availableAmount)
        : undefined;
      // const sumAjusments =
      //   paymentAdjustments?.reduce(
      //     (sum, { balanceTransaction }) => sum + balanceTransaction.amount,
      //     0
      //   ) || 0;

      // const limit = {
      //   min: Number(round(amount - extraAvailable - sumAjusments)),
      //   max: Number(round(amount - sumAjusments - 0.01)),
      // };

      const amountMax = (() => {
        if (formData?.billId && agreementBills?.length && availableAmount) {
          const selectedBill = agreementBills.find(({ id }) => id === formData.billId);
          if (selectedBill) {
            return Math.min(selectedBill.amountLeft, availableAmount);
          }
        }
        return availableAmount;
      })();

      return {
        availableAmount,
        // limit,
        // limitAdj: {
        amountMin: 0.01,
        amountMax,
        // },
        // reversalIsAvailable: amount === extraAvailable,
        // reversalIsAvailable: true,
      };
    }
  }, [payment, formData?.billId, agreementBills]);

  const validateFormField = (fieldName: keyof FormData) => {
    if (formData?.[fieldName] !== undefined) {
      form.validateFields([fieldName]);
    }
  };

  const onChangePaymentGoalTypeId = (value: number) => {
    dispatch(accountPaymentDetailsActions.cleanAgreementBills());
    dispatch(accountPaymentDetailsActions.cleanAgreementServices());
    setFormFieldsValue({ priority: "", billId: undefined });
    validateFormField("amountForPaymentGoal");
    switch (value) {
      case 1:
        setFormFieldsValue({ priority: "0" });
        break;
      case 2:
        setFormFieldsValue({ priority: "1" });
        break;
      case 5:
        setFormFieldsValue({ priority: "0" });
        break;
      case 6:
        setFormFieldsValue({ priority: "0" });
        break;
      default:
        break;
    }
  };

  const handleSubmit = (values: FormData) => {
    const {
      paymentGoalTypeId,
      reason,
      billId,
      amountForPaymentGoal,
      // goalAmountCalculate,
      serviceId,
      priority,
    } = values;
    if ([1, 2].includes(paymentGoalTypeId)) {
      submitCreateBillGoal({
        useNonTargeted: false,
        reason,
        coverBills: [
          {
            paymentId: payment!.id,
            billId,
            amountForPaymentGoal: +amountForPaymentGoal,
            goalAmountCalculate: false,
            order: 1,
          },
        ],
      });
    } else if (paymentGoalTypeId === 5) {
      const service = agreementServices?.find(({ id }) => id === serviceId);
      if (service) {
        submitCreateOfferGoal({
          useNonTargeted: false,
          reason,
          coverOfferings: [
            {
              paymentId: payment!.id,
              offeringId: service.offering.id!,
              amountForPaymentGoal: +amountForPaymentGoal,
              goalAmountCalculate: false,
              order: 1,
              priority: +priority,
            },
          ],
        });
      }
    } else if (paymentGoalTypeId === 6) {
      const service = agreementServices?.find(({ id }) => id === serviceId);
      if (service) {
        submitCreateServiceGoal({
          useNonTargeted: false,
          reason,
          coverServices: [
            {
              paymentId: payment!.id,
              serviceId,
              amountForPaymentGoal: +amountForPaymentGoal,
              goalAmountCalculate: false,
              order: 1,
              priority: +priority,
            },
          ],
        });
      }
    }
  };

  return (
    <Modal
      className={combineClasses("create-goal-modal", className)}
      visible={visible}
      title="Создание цели"
      onCancel={closeModal}
      footer={
        <>
          <Button onClick={closeModal}>Отмена</Button>
          <Button type="primary" onClick={form.submit} loading={isLoading}>
            Сохранить
          </Button>
        </>
      }
    >
      <Form form={form} labelAlign="left" onValuesChange={onValuesChange} onFinish={handleSubmit}>
        <Form.Item
          label="Доступная сумма"
          {...formItemProps}
          rules={[
            {
              required: true,
              message: "",
            },
          ]}
        >
          {formatNumber(paymentData?.availableAmount || 0)}
        </Form.Item>
        <Form.Item
          label="Тип цели"
          name="paymentGoalTypeId"
          {...formItemProps}
          rules={[
            {
              required: true,
              message: "",
            },
          ]}
        >
          <Select
            options={getSelectOptions({
              list: paymentGoalTypes,
            })}
            onChange={onChangePaymentGoalTypeId}
          />
        </Form.Item>
        {[1, 2].includes(formData?.paymentGoalTypeId as number) && (
          <Form.Item
            label="Счет"
            name="billId"
            {...formItemProps}
            rules={[
              {
                required: true,
                message: "",
              },
            ]}
          >
            <Select
              loading={checkIsLoading(agreementBillsLoading)}
              options={getSelectOptions({
                list: agreementBillList,
                labelName: "label",
              })}
              onSelect={() => {
                validateFormField("amountForPaymentGoal");
              }}
            />
          </Form.Item>
        )}
        {[5, 6].includes(formData?.paymentGoalTypeId as number) && (
          <>
            <Form.Item
              label={formData?.paymentGoalTypeId === 6 ? "Продукт" : "Предложение для продукта"}
              name="serviceId"
              {...formItemProps}
              className="create-goal-modal__serviceId"
              rules={[
                {
                  required: true,
                  message: "",
                },
              ]}
            >
              <Select
                loading={checkIsLoading(agreementServicesLoading)}
                options={getSelectOptions({
                  list: agreementServiceList,
                  labelName: "label",
                })}
              />
            </Form.Item>
            <div className="create-goal-modal__checker create-goal-modal__includePastDates">
              <Form.Item
                name="includePastDates"
                valuePropName="checked"
                noStyle
                id="includePastDates"
              >
                <Checkbox
                  onChange={() => {
                    setFormFieldsValue({
                      serviceId: undefined,
                    });
                  }}
                />
              </Form.Item>
              <label className="create-goal-modal__checker-title" htmlFor="includePastDates">
                Только закрытые
              </label>
            </div>
          </>
        )}

        <Form.Item
          label="Приоритет"
          name="priority"
          {...formItemProps}
          rules={[
            {
              required: true,
              message: "",
            },
          ]}
        >
          <InputNumberFormat
            allowNegative={false}
            decimalScale={0}
            // disabled={[1, 2].includes(formData?.paymentGoalTypeId as number)}
            disabled
          />
        </Form.Item>
        <Form.Item
          label="Сумма цели"
          name="amountForPaymentGoal"
          {...formItemProps}
          rules={[
            {
              required: true,
              message: "",
            },
            {
              validator: (_, value) => {
                const valueNum = Number(value);
                if (valueNum) {
                  if (!paymentData) {
                    return Promise.resolve();
                  }
                  if (valueNum < paymentData.amountMin || valueNum > (paymentData.amountMax ?? 0)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                }
                return Promise.reject();
              },
              message: "",
            },
          ]}
        >
          <InputNumberFormat
            allowNegative={false}
            placeholder={
              paymentData
                ? `${formatNumber(paymentData.amountMin)} - ${formatNumber(
                    paymentData.amountMax ?? 0
                  )}`
                : ""
            }
          />
        </Form.Item>
        <Form.Item label="Обоснование" name="reason" {...formItemProps}>
          <Input.TextArea />
        </Form.Item>
        {/* {![1, 2].includes(formData?.paymentGoalTypeId as number) && (
          <div className="create-goal-modal__checker">
            <Form.Item
              name="goalAmountCalculate"
              valuePropName="checked"
              noStyle
              id="goalAmountCalculate"
            >
              <Checkbox />
            </Form.Item>
            <label className="create-goal-modal__checker-title" htmlFor="goalAmountCalculate">
              Рассчитывать сумму цели по доступным остаткам
            </label>
          </div>
        )} */}
      </Form>
      <FormInfo getFormValues={() => form.getFieldsValue()} />
    </Modal>
  );
};
